import React, { useEffect, useState } from "react";

import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useDebounce } from "use-debounce";

import { blue, grey, red } from "../../../../../utils/colors";

const rangeStyle = css`
    input[type="range"] {
        width: 100%;
        height: 7px;
        margin-right: 15px;
        background: ${grey} linear-gradient(${red}, ${red}) no-repeat;
        background-size: 70% 100%;
        border-radius: 5px;
        appearance: none;
    }

    /* Input Thumb */
    input[type="range"]::-webkit-slider-thumb {
        width: 20px;
        height: 20px;
        cursor: ew-resize;
        background: ${blue};
        border-radius: 50%;
        appearance: none;
    }

    input[type="range"]::-moz-range-thumb {
        width: 20px;
        height: 20px;
        cursor: ew-resize;
        background: ${blue};
        border-radius: 50%;
        appearance: none;
    }

    input[type="range"]::-ms-thumb {
        width: 20px;
        height: 20px;
        cursor: ew-resize;
        background: ${blue};
        border-radius: 50%;
        appearance: none;
    }

    /* Input Track */
    input[type="range"]::-webkit-slider-runnable-track {
        background: transparent;
        border: none;
        box-shadow: none;
        appearance: none;
    }

    input[type="range"]::-moz-range-track {
        background: transparent;
        border: none;
        box-shadow: none;
        appearance: none;
    }

    input[type="range"]::-ms-track {
        background: transparent;
        border: none;
        box-shadow: none;
        appearance: none;
    }
`;

const Grid = styled.div`
    display: grid;
`;

const SpaceBetween = styled.div`
    display: flex;
    justify-content: space-between;
    color: ${({ theme }) => theme.colors.blue.toString()};
`;

interface iRangeSliderComponent {
    value: number;
    min: number;
    max: number;
    step: number;
    title: string;
    unit: string;
    onChange: (value: number) => void;
}

const RangeSlider = ({
    value = 0,
    min = 0,
    max = 100,
    step = 1,
    title = "",
    unit = "",
    onChange,
    ...props
}: iRangeSliderComponent) => {
    const [localValue, setLocalValue] = useState(value);
    const [debouncedValue] = useDebounce(localValue, 500);

    useEffect(() => {
        if (onChange !== undefined) {
            onChange(localValue);
        }
    }, [debouncedValue]);

    useEffect(() => {
        //Apply the custom slider background on element creation:
        const input = document.querySelector<HTMLElement>(".range-slider");
        if (input) {
            input.style.backgroundSize =
                ((value - min) * 100) / (max - min) + "% 100%";
        }
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const target = e.target;
        const min = parseInt(target.min);
        const max = parseInt(target.max);
        const val = parseInt(target.value);

        target.style.backgroundSize =
            ((val - min) * 100) / (max - min) + "% 100%";
        setLocalValue(val);
    };

    return (
        <Grid>
            <SpaceBetween>
                <span>{title}</span>
                <span>
                    {localValue} {unit}
                </span>
            </SpaceBetween>

            <div css={rangeStyle}>
                <input
                    className="range-slider"
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={localValue}
                    onChange={handleChange}
                    {...props}
                />
            </div>
        </Grid>
    );
};

export default RangeSlider;
